import { useEffect } from 'react';
import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';

// Status options
const statusOptions = [
  { value: 'ACTIVE', text: 'Active' },
  { value: 'INACTIVE', text: 'Inactive' },
];

const UserRolesModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  defaultValues,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      role: '',
      description: '',
      access_id: '',
      access_json: '',
      status: 'ACTIVE',
    },
  });

  // Reset form when modal opens or defaultValues change
  useEffect(() => {
    if (isOpen && defaultValues) {
      reset(defaultValues);
    }
  }, [isOpen, defaultValues, reset]);

  const handleClose = () => {
    reset({
      role: '',
      description: '',
      access_id: '',
      access_json: '',
      status: 'ACTIVE',
    });
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 w-full md:w-[800px] max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Modal Title and Close Button */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-800">
              {isEditMode ? 'Edit User Role' : 'Add User Role'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          <form
            className="flex flex-col gap-4 md:gap-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Row 1: Role */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="role" className="text-text-hint mb-1">
                Role <span className="text-danger-500">*</span>
              </label>
              <input
                {...register('role', {
                  required: 'Role is required',
                  minLength: { value: 2, message: 'Role must be at least 2 characters' },
                  maxLength: { value: 15, message: 'Max 15 characters' },
                })}
                type="text"
                placeholder="Enter role name"
                id="role"
                className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
              />
              {errors.role && (
                <span className="text-red-500 text-xs">{errors.role.message}</span>
              )}
            </div>

            {/* Row 2: Description */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="description" className="text-text-hint mb-1">
                Description
              </label>
              <textarea
                {...register('description', {
                  maxLength: { value: 25, message: 'Max 25 characters' },
                })}
                placeholder="Enter role description"
                id="description"
                rows={3}
                className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black resize-none"
              />
              {errors.description && (
                <span className="text-red-500 text-xs">{errors.description.message}</span>
              )}
            </div>

            {/* Row 3: Access ID */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="access_id" className="text-text-hint mb-1">
                Access ID
              </label>
              <input
                {...register('access_id')}
                type="number"
                placeholder="e.g., 1"
                id="access_id"
                className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
              />
            </div>

            {/* Row 4: Access JSON */}
            <div className="flex flex-col space-y-1">
              <label htmlFor="access_json" className="text-text-hint mb-1">
                Access JSON (Permissions)
              </label>
              <textarea
                {...register('access_json')}
                placeholder='{"modules": ["dashboard", "users"], "permissions": {"create": true, "read": true}}'
                id="access_json"
                rows={5}
                className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black resize-none font-mono text-sm"
              />
            </div>

            {/* Row 4: Status */}
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint mb-1">
                Status <span className="text-danger-500">*</span>
              </label>
              <ul className="grid grid-cols-2 w-64">
                {statusOptions.map((option, index) => (
                  <li key={option.value} className="relative">
                    <input
                      {...register('status', { required: 'Status is required' })}
                      className="sr-only peer"
                      type="radio"
                      value={option.value}
                      id={`status-${option.value}`}
                    />
                    <label
                      className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800 ${
                        index === 0
                          ? 'rounded-tl-full rounded-bl-full'
                          : 'rounded-tr-full rounded-br-full'
                      }`}
                      htmlFor={`status-${option.value}`}
                    >
                      {option.text}
                    </label>
                  </li>
                ))}
              </ul>
              {errors.status && (
                <span className="text-red-500 text-xs">{errors.status.message}</span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-secondary-700 text-text-light py-2 px-6 rounded-lg shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? isEditMode
                      ? 'Updating...'
                      : 'Saving...'
                    : isEditMode
                    ? 'Update'
                    : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default UserRolesModal;
